import type { TLayout, TSection } from '../models/playlist'
import { assetMatchesSectionAspectRatio } from './assetMatchesSectionAspectRatio'

/** `contain` letterboxes; `cover` crops to fill the zone. */
export type TSectionFit = 'contain' | 'cover'

export type TSectionFitMedia = 'image' | 'video'

export type TSectionFitWarning = 'cropped' | 'bars'

/** Images historically fill-and-crop; videos historically letterbox. */
export function defaultSectionFit(media: TSectionFitMedia): TSectionFit {
  return media === 'image' ? 'cover' : 'contain'
}

/** Explicit zone fit, or the historical default for that media type. */
export function resolveSectionFit(
  fit: TSection['fit'] | null | undefined,
  media: TSectionFitMedia
): TSectionFit {
  return fit === 'contain' || fit === 'cover' ? fit : defaultSectionFit(media)
}

export function sectionObjectFitClass(fit: TSectionFit): string {
  return fit === 'cover' ? 'object-cover object-center' : 'object-contain object-center'
}

/** Operator warning for a mismatched zone, or null when the shapes already agree. */
export function sectionFitWarning(opts: {
  fit: TSection['fit'] | null | undefined
  media: TSectionFitMedia
  mediaWidth: number
  mediaHeight: number
  zoneWidth: number
  zoneHeight: number
}): TSectionFitWarning | null {
  if (!(opts.mediaWidth > 0 && opts.mediaHeight > 0 && opts.zoneWidth > 0 && opts.zoneHeight > 0)) {
    return null
  }
  if (assetMatchesSectionAspectRatio(
    { width: opts.zoneWidth, height: opts.zoneHeight },
    { width: opts.mediaWidth, height: opts.mediaHeight }
  )) {
    return null
  }
  return resolveSectionFit(opts.fit, opts.media) === 'cover' ? 'cropped' : 'bars'
}

/**
 * Catalog layouts have no per-frame fit. Stamp the frame copy's fit onto matching zones
 * so playback and fullscreen previews match the storyboard.
 */
export function layoutPreviewWithSectionFit(catalog: TLayout, frameLayout: TLayout): TLayout {
  const sections: TLayout['sections'] = { ...catalog.sections }
  let stamped = false
  for (const [key, section] of Object.entries(frameLayout.sections)) {
    const fit = section.fit
    const zone = sections[key]
    if (fit == null || zone == null || zone.fit === fit) {
      continue
    }
    sections[key] = { ...zone, fit }
    stamped = true
  }
  return stamped ? { ...catalog, sections } : catalog
}
